class CtrCharts extends HTMLElement {
  static INACTIVE_OPACITY = 0.72;

  constructor() {
    super();
    this.segments = [];
    this.activeIndex = 0;
    this.chart = null;
    this.onLegendEnter = this.onLegendEnter.bind(this);
  }

  connectedCallback() {
    this.parseConfig();
    this.createChart();
    this.bindLegend();
    this.setActive(this.activeIndex);
  }

  disconnectedCallback() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  parseConfig() {
    const configEl = this.querySelector('[ref="segmentsConfig"]');

    try {
      const config = JSON.parse(configEl?.textContent || '[]');
      this.segments = Array.isArray(config) ? config : [];
    } catch {
      this.segments = [];
    }

    this.activeIndex = Number.parseInt(this.dataset.defaultIndex, 10) || 0;
  }

  colorWithOpacity(color, opacity) {
    if (!color) return color;

    const hex = color.trim();
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (match) {
      const r = Number.parseInt(match[1], 16);
      const g = Number.parseInt(match[2], 16);
      const b = Number.parseInt(match[3], 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    return color;
  }

  getSegmentColors(activeIndex = this.activeIndex) {
    return this.segments.map((segment, index) => {
      const color = segment.color || '#000000';
      return index === activeIndex
        ? color
        : this.colorWithOpacity(color, CtrCharts.INACTIVE_OPACITY);
    });
  }

  createChart() {
    const canvas = this.querySelector('[ref="chartCanvas"]');
    if (!canvas || !window.Chart || !this.segments.length) return;

    this.chart = new window.Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: this.segments.map((segment) => segment.label || ''),
        datasets: [
          {
            data: this.segments.map((segment) => parseFloat(segment.value) || 0),
            backgroundColor: this.getSegmentColors(),
            borderWidth: 0,
            spacing: 4,
            hoverOffset: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '63.5%',
        animation: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        onHover: (event, elements) => {
          if (elements.length) {
            this.setActive(elements[0].index);
          }
        },
      },
    });
  }

  bindLegend() {
    this.querySelectorAll('.ctr-charts__legend-row[data-segment-index]').forEach((element) => {
      element.addEventListener('mouseenter', this.onLegendEnter);
      element.addEventListener('focus', this.onLegendEnter);
      element.addEventListener('click', this.onLegendEnter);
    });
  }

  onLegendEnter(event) {
    const index = Number.parseInt(event.currentTarget.dataset.segmentIndex, 10);
    this.setActive(index);
  }

  setActive(index) {
    if (index < 0 || index >= this.segments.length) return;

    this.activeIndex = index;
    this.updateLegendState();
    this.updateTooltip();

    if (!this.chart) return;

    const dataset = this.chart.data.datasets[0];
    dataset.backgroundColor = this.getSegmentColors(index);
    this.chart.setActiveElements([{ datasetIndex: 0, index }]);
    this.chart.update('none');
  }

  updateLegendState() {
    this.querySelectorAll('.ctr-charts__legend-row').forEach((element) => {
      const index = Number.parseInt(element.dataset.segmentIndex, 10);
      element.classList.toggle('is-active', index === this.activeIndex);
    });
  }

  updateTooltip() {
    const segment = this.segments[this.activeIndex];
    const label = this.querySelector('[ref="tooltipLabel"]');
    const value = this.querySelector('[ref="tooltipValue"]');

    if (!segment || !label || !value) return;

    label.textContent = segment.label || '';
    value.textContent = segment.displayValue || '';
  }
}

if (!customElements.get('ctr-charts')) {
  customElements.define('ctr-charts', CtrCharts);
}
